import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, ArrowRight, Eye, EyeOff, HeartHandshake } from "lucide-react";

const NAVY = "#032866";
const GOLD = "#fdda0d";
const GOLD_DEEP = "#e8b400";

// Dedicated caregiver (PCW) login — a SEPARATE realm from the office
// staff login (Staff/PCW isolation, Phase 1). Field accounts sign in
// here; the backend rejects them from the office /auth/login and vice
// versa, and confines their token to the field API surface.
export default function PcwLogin() {
  const { pcwLogin, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Already signed in: send field users to the portal, office users to their app.
  if (isAuthenticated) {
    return <Navigate to={user?.role === "pcw" ? "/healthcare/pcw-portal" : "/dashboard"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await pcwLogin(formData.email, formData.password);
      if (result?.requires_2fa) {
        toast.info("This account uses two-factor sign-in. Please contact your coordinator.");
        return;
      }
      navigate("/healthcare/pcw-portal", { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="pcw-login-page">
      <div className="w-full max-w-md">
        <div
          className="rounded-3xl shadow-xl border border-slate-100 bg-white overflow-hidden"
          data-testid="pcw-login-card"
        >
          <div className="px-8 pt-8 pb-6 text-center" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #06407f 100%)` }}>
            <div
              className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-4"
              style={{ background: GOLD }}
            >
              <HeartHandshake className="w-7 h-7" style={{ color: NAVY }} />
            </div>
            <h1 className="text-xl font-black text-white tracking-tight" data-testid="pcw-login-title">
              Caregiver Sign-In
            </h1>
            <p className="text-[13px] text-white/70 font-medium mt-1">
              Avida Health · clock in and see your visits
            </p>
          </div>

          <div className="px-8 py-8">
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="pcw-login-form">
              <div>
                <label className="text-slate-700 text-xs font-bold tracking-wide uppercase mb-2 block">Email</label>
                <input
                  type="email"
                  required
                  autoComplete="username"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@avidahealth.org"
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 text-sm font-semibold focus:outline-none focus:border-slate-400 transition-colors"
                  data-testid="pcw-email-input"
                />
              </div>
              <div>
                <label className="text-slate-700 text-xs font-bold tracking-wide uppercase mb-2 block">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter your password"
                    className="w-full h-12 px-4 pr-12 rounded-xl border-2 border-slate-200 text-sm font-semibold focus:outline-none focus:border-slate-400 transition-colors"
                    data-testid="pcw-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="pcw-toggle-password-btn"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-xl font-black text-sm shadow-lg transition-all hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`, color: NAVY }}
                data-testid="pcw-login-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing you in…</>
                ) : (
                  <>Sign in<ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-[12.5px] font-semibold text-slate-500 hover:text-slate-800 transition-colors"
                data-testid="staff-login-link"
              >
                Office staff? Use the staff sign-in →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
